<script>
  import { _ } from '../../lib/i18n.js';
  import {
    Search, ChevronDown, ChevronUp, ChevronRight, Download, X, Server, ShieldCheck,
  } from 'lucide-svelte';
  import {
    severityColors, severityRail, severityOrder, findingStatusColors, fallbackBadge,
  } from '../../lib/complianceMaps.js';
  import { isAggregateFinding, resolvedRowsFromDelta, weightOf } from '../../lib/complianceMetrics.js';

  // findings: pre-flattened rows (each tagged sectionKey/sectionTitle). May
  // include synthetic aggregate rows — we drop those so every table row is a
  // real CVE; the "more vulns exist" signal lives in the section metrics.
  export let findings = [];
  export let delta = null;
  export let scopeMode = null;
  export let hosts = [];
  // Deep-link intent from the Overview tab: { severity?: string[], section?: string }.
  export let intent = null;
  export let reportId = '';

  // ---- date / formatting helpers (mirror the viewer; date-only safe) ----
  function formatDate(value) {
    if (!value) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
    return d.toLocaleDateString('it-IT', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  const pretty = (v) => String(v ?? '').replace(/[-_]/g, ' ');
  const sevLabel = (s) => (s && severityColors[s] ? $_('complianceReports.severity.' + s) : pretty(s));
  const statusLabel = (s) => (s && findingStatusColors[s] ? $_('complianceReports.findingStatus.' + s) : pretty(s));

  // ---- filter / sort state ----
  let search = '';
  let sevOn = { critical: true, high: true, medium: true, low: true, info: true };
  let statusOn = { open: true, 'in-progress': true, accepted: true };
  let includeResolved = false;
  let hostFilter = '';
  let sectionFilter = '';
  let groupBySeverity = false;
  let sortKey = 'severity';
  let sortDir = 'asc';
  let expandedKey = null;
  let page = 1;

  const PAGE_SIZE = 50;
  const THRESHOLD = 60;

  // Real per-CVE findings only (drop synthetic rollups).
  $: realFindings = (findings || []).filter((f) => !isAggregateFinding(f));

  // Resolved pseudo-rows from the delta, de-duped against live findings.
  $: resolvedRows = (() => {
    if (!includeResolved) return [];
    const live = new Set(realFindings.map((f) => `${f.sectionKey}::${f.id}`));
    return resolvedRowsFromDelta(delta).filter((r) => !live.has(`${r.sectionKey}::${r.id}`));
  })();

  $: dataset = [...realFindings, ...resolvedRows];

  // Distinct sections / statuses present, for the filter controls.
  $: sections = [...new Map(realFindings.map((f) => [f.sectionKey, f.sectionTitle])).entries()]
    .map(([key, title]) => ({ key, title }));
  $: statusesPresent = [...new Set(realFindings.map((f) => f.status || 'open'))].filter((s) => s !== 'resolved');
  $: sevCounts = severityOrder.reduce((acc, s) => {
    acc[s] = realFindings.filter((f) => f.severity === s).length;
    return acc;
  }, {});

  // Column visibility — keep the table lean for unstamped / hostless reports.
  $: showHostCol = dataset.some((f) => (f.affectedHosts || []).length);
  $: showLifecycle = dataset.some((f) => f.firstSeen || f.lastSeen);
  $: showResolvedCol = dataset.some((f) => f.resolvedAt) || includeResolved;
  $: showHostSelect = (scopeMode === 'all' || scopeMode === 'host') && hosts.length > 0;

  function matches(f) {
    // Resolved rows are gated solely by the include-resolved toggle.
    if (f.status === 'resolved') {
      // still subject to host/section/search/severity below
    } else if (!statusOn[f.status || 'open']) {
      return false;
    }
    if (f.severity != null && !sevOn[f.severity]) return false;
    // Resolved rows come from summary.delta and carry no host attribution, so the
    // host filter must not silently drop them.
    if (hostFilter && f.status !== 'resolved' && !(f.affectedHosts || []).includes(hostFilter)) return false;
    if (sectionFilter && f.sectionKey !== sectionFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${f.id ?? ''} ${f.title ?? ''} ${(f.affectedHosts || []).join(' ')}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }

  $: filtered = dataset.filter(matches);

  function dateVal(v) {
    if (!v) return Infinity;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? Infinity : t;
  }
  const statusRank = { open: 0, 'in-progress': 1, accepted: 2, resolved: 3 };

  function cmp(a, b) {
    let av;
    let bv;
    switch (sortKey) {
      case 'status': av = statusRank[a.status] ?? 9; bv = statusRank[b.status] ?? 9; break;
      case 'firstSeen':
      case 'lastSeen':
      case 'resolvedAt': av = dateVal(a[sortKey]); bv = dateVal(b[sortKey]); break;
      case 'section': av = a.sectionTitle || ''; bv = b.sectionTitle || ''; break;
      case 'id': av = a.id || ''; bv = b.id || ''; break;
      case 'title': av = a.title || ''; bv = b.title || ''; break;
      default: av = weightOf(a.severity); bv = weightOf(b.severity);
    }
    let r = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
    if (r === 0) r = String(a.id || '').localeCompare(String(b.id || ''));
    return sortDir === 'desc' ? -r : r;
  }

  $: sorted = [...filtered].sort(cmp);

  // Reset to page 1 whenever the filtered set changes.
  $: { filtered; page = 1; }

  $: paginate = !groupBySeverity && sorted.length > THRESHOLD;
  $: totalPages = paginate ? Math.ceil(sorted.length / PAGE_SIZE) : 1;
  $: pageRows = paginate ? sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : sorted;

  // Group-by-severity view (paginations disabled while grouped).
  $: groups = groupBySeverity
    ? [...severityOrder, null]
        .map((sev) => ({
          sev,
          label: sev ? $_('complianceReports.severity.' + sev) : $_('complianceReports.findingStatus.resolved'),
          rows: sorted.filter((f) => (f.severity ?? null) === sev),
        }))
        .filter((g) => g.rows.length)
    : null;

  function toggleSort(key) {
    if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    else { sortKey = key; sortDir = 'asc'; }
  }
  const rowKey = (f) => `${f.sectionKey}::${f.id}`;
  function toggleExpand(f) {
    const k = rowKey(f);
    expandedKey = expandedKey === k ? null : k;
  }

  function toggleSev(s) { sevOn = { ...sevOn, [s]: !sevOn[s] }; }
  function toggleStatus(s) { statusOn = { ...statusOn, [s]: !statusOn[s] }; }

  function resetFilters() {
    search = '';
    sevOn = { critical: true, high: true, medium: true, low: true, info: true };
    statusOn = { open: true, 'in-progress': true, accepted: true };
    includeResolved = false;
    hostFilter = '';
    sectionFilter = '';
    appliedIntent = intent; // swallow the active deep-link so it doesn't re-apply
  }

  // ---- deep-link intent from the Overview tab ----
  let appliedIntent = null;
  $: if (intent && intent !== appliedIntent) {
    appliedIntent = intent;
    if (Array.isArray(intent.severity)) {
      sevOn = severityOrder.reduce((acc, s) => { acc[s] = intent.severity.includes(s); return acc; }, {});
    }
    if (intent.section !== undefined) sectionFilter = intent.section || '';
  }

  $: anyFilterActive = !!search || !!hostFilter || !!sectionFilter || includeResolved
    || severityOrder.some((s) => !sevOn[s]) || statusesPresent.some((s) => !statusOn[s]);

  // ---- CSV export of the current (filtered + sorted) view ----
  function csvCell(v) {
    let s = v == null ? '' : String(v);
    // Neutralise formula-leading characters before the quoting decision: a
    // spreadsheet treats a cell starting with any of these as a formula/DDE
    // trigger regardless of quoting, and findings arrive from a machine
    // ingest endpoint whose payload we do not otherwise sanitise.
    if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }
  function exportCsv() {
    const cols = ['severity', 'id', 'title', 'status', 'section', 'hosts', 'firstSeen', 'lastSeen', 'resolvedAt', 'remediation'];
    const lines = [cols.join(',')];
    for (const f of sorted) {
      lines.push([
        f.severity, f.id, f.title, f.status, f.sectionTitle,
        (f.affectedHosts || []).join('; '), f.firstSeen, f.lastSeen, f.resolvedAt, f.remediation,
      ].map(csvCell).join(','));
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportId || 'report'}-findings.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function SortIcon(key) { return sortKey === key ? (sortDir === 'asc' ? ChevronUp : ChevronDown) : null; }
</script>

{#if realFindings.length === 0}
  <div class="py-12 text-center">
    <ShieldCheck size={40} class="mx-auto text-emerald-400 mb-3" />
    <p class="text-gray-500 dark:text-gray-400">{$_('complianceReports.findings.noFindings')}</p>
  </div>
{:else}
  <!-- Toolbar -->
  <div class="flex flex-wrap items-center gap-2 mb-3">
    <div class="relative">
      <Search size={15} class="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      <input
        type="text"
        class="input !w-56 !pl-7 !py-1.5 text-sm"
        placeholder={$_('common.search')}
        bind:value={search}
      />
    </div>

    {#each severityOrder as s}
      {#if sevCounts[s]}
        <button
          type="button"
          class="px-2 py-1 text-xs font-bold border rounded transition-opacity {sevOn[s] ? (severityColors[s] || fallbackBadge) : 'border-gray-300 dark:border-gray-600 text-gray-400 opacity-50'}"
          on:click={() => toggleSev(s)}
        >
          {sevCounts[s]} {$_('complianceReports.severity.' + s)}
        </button>
      {/if}
    {/each}

    {#if showHostSelect}
      <select class="input !w-auto !py-1.5 text-sm" bind:value={hostFilter}>
        <option value="">{$_('complianceReports.findings.allHosts')}</option>
        {#each hosts as h}<option value={h}>{h}</option>{/each}
      </select>
    {/if}

    {#if sections.length > 1}
      <select class="input !w-auto !py-1.5 text-sm" bind:value={sectionFilter}>
        <option value="">{$_('complianceReports.findings.allSections')}</option>
        {#each sections as s}<option value={s.key}>{s.title}</option>{/each}
      </select>
    {/if}

    {#if delta?.resolvedFindingIds?.length}
      <label class="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none">
        <input type="checkbox" bind:checked={includeResolved} class="accent-primary-500" />
        {$_('complianceReports.findings.includeResolved')}
      </label>
    {/if}

    <label class="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none">
      <input type="checkbox" bind:checked={groupBySeverity} class="accent-primary-500" />
      {$_('complianceReports.findings.groupBySeverity')}
    </label>

    <div class="ml-auto flex items-center gap-2">
      <span class="text-xs text-gray-400">
        {$_('complianceReports.findings.showing', { values: { shown: sorted.length, total: realFindings.length } })}
      </span>
      {#if anyFilterActive}
        <button type="button" class="text-xs text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1" on:click={resetFilters}>
          <X size={12} /> {$_('complianceReports.findings.resetFilters')}
        </button>
      {/if}
      <button type="button" class="btn btn-secondary !py-1 !px-2 text-xs inline-flex items-center gap-1" on:click={exportCsv}>
        <Download size={13} /> {$_('complianceReports.findings.exportCsv')}
      </button>
    </div>
  </div>

  <!-- Status chips (only when more than one live status exists) -->
  {#if statusesPresent.length > 1}
    <div class="flex flex-wrap items-center gap-1.5 mb-3">
      {#each statusesPresent as s}
        <button
          type="button"
          class="px-2 py-0.5 text-[11px] font-bold border rounded transition-opacity {statusOn[s] ? (findingStatusColors[s] || fallbackBadge) : 'border-gray-300 dark:border-gray-600 text-gray-400 opacity-50'}"
          on:click={() => toggleStatus(s)}
        >
          {statusLabel(s)}
        </button>
      {/each}
    </div>
  {/if}

  <!-- Table -->
  {#if sorted.length === 0}
    <div class="py-10 text-center">
      <p class="text-gray-500 dark:text-gray-400 mb-2">{$_('complianceReports.findings.noMatch')}</p>
      <button type="button" class="text-sm text-primary-600 dark:text-primary-400 hover:underline" on:click={resetFilters}>
        {$_('complianceReports.findings.resetFilters')}
      </button>
    </div>
  {:else}
    <div class="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
      <table class="w-full text-sm">
        <thead class="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
          <tr>
            <th class="w-1 p-0" aria-hidden="true"></th>
            <th class="px-2 py-2">
              <button type="button" class="inline-flex items-center gap-1 select-none uppercase" on:click={() => toggleSort('severity')}>{$_('complianceReports.severityCol')}{#if SortIcon('severity')}<svelte:component this={SortIcon('severity')} size={12} />{/if}</button>
            </th>
            <th class="px-2 py-2">
              <button type="button" class="inline-flex items-center gap-1 select-none uppercase" on:click={() => toggleSort('id')}>{$_('complianceReports.idCve')}{#if SortIcon('id')}<svelte:component this={SortIcon('id')} size={12} />{/if}</button>
            </th>
            <th class="px-2 py-2">
              <button type="button" class="inline-flex items-center gap-1 select-none uppercase" on:click={() => toggleSort('title')}>{$_('complianceReports.findings.title')}{#if SortIcon('title')}<svelte:component this={SortIcon('title')} size={12} />{/if}</button>
            </th>
            {#if showHostCol}<th class="px-2 py-2">{$_('complianceReports.affectedHosts')}</th>{/if}
            {#if sections.length > 1}
              <th class="px-2 py-2">
                <button type="button" class="inline-flex items-center gap-1 select-none uppercase" on:click={() => toggleSort('section')}>{$_('complianceReports.sectionCol')}{#if SortIcon('section')}<svelte:component this={SortIcon('section')} size={12} />{/if}</button>
              </th>
            {/if}
            {#if showLifecycle}
              <th class="px-2 py-2 whitespace-nowrap">
                <button type="button" class="inline-flex items-center gap-1 select-none uppercase" on:click={() => toggleSort('firstSeen')}>{$_('complianceReports.firstSeen')}{#if SortIcon('firstSeen')}<svelte:component this={SortIcon('firstSeen')} size={12} />{/if}</button>
              </th>
              <th class="px-2 py-2 whitespace-nowrap">
                <button type="button" class="inline-flex items-center gap-1 select-none uppercase" on:click={() => toggleSort('lastSeen')}>{$_('complianceReports.lastSeen')}{#if SortIcon('lastSeen')}<svelte:component this={SortIcon('lastSeen')} size={12} />{/if}</button>
              </th>
            {/if}
            {#if showResolvedCol}
              <th class="px-2 py-2 whitespace-nowrap">
                <button type="button" class="inline-flex items-center gap-1 select-none uppercase" on:click={() => toggleSort('resolvedAt')}>{$_('complianceReports.resolvedAt')}{#if SortIcon('resolvedAt')}<svelte:component this={SortIcon('resolvedAt')} size={12} />{/if}</button>
              </th>
            {/if}
            <th class="w-8 px-2 py-2" aria-hidden="true"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
          {#each (groups ?? [{ sev: '_', rows: pageRows }]) as group}
            {#if groups}
              <tr class="bg-gray-100 dark:bg-gray-800/60">
                <td class="w-1 p-0 {group.sev ? severityRail[group.sev] || '' : ''}"></td>
                <td colspan="12" class="px-2 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {group.label} · {group.rows.length}
                </td>
              </tr>
            {/if}
            {#each group.rows as f (rowKey(f))}
              {@const k = rowKey(f)}
              <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50 align-top">
                <td class="w-1 p-0 {f.severity ? severityRail[f.severity] || '' : 'bg-gray-300 dark:bg-gray-600'}"></td>
                <td class="px-2 py-2 whitespace-nowrap">
                  {#if f.severity}
                    <span class="px-1.5 py-0.5 text-[11px] font-bold border rounded {severityColors[f.severity] || fallbackBadge}">{sevLabel(f.severity)}</span>
                  {/if}
                  {#if f.status && f.status !== 'open'}
                    <span class="ml-1 px-1.5 py-0.5 text-[10px] font-semibold border rounded {findingStatusColors[f.status] || fallbackBadge}">{statusLabel(f.status)}</span>
                  {/if}
                </td>
                <td class="px-2 py-2 font-mono text-xs text-gray-600 dark:text-gray-300 max-w-[10rem] truncate" title={f.id}>{f.id}</td>
                <td class="px-2 py-2 text-gray-800 dark:text-gray-200 max-w-sm truncate" title={f.title}>{f.title}</td>
                {#if showHostCol}
                  <td class="px-2 py-2 whitespace-nowrap" title={(f.affectedHosts || []).join(', ')}>
                    {#if (f.affectedHosts || []).length}
                      <span class="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                        <Server size={11} class="text-gray-400" />
                        {f.affectedHosts.slice(0, 2).join(', ')}{#if f.affectedHosts.length > 2}<span class="text-gray-400"> +{f.affectedHosts.length - 2}</span>{/if}
                      </span>
                    {:else}<span class="text-gray-400">—</span>{/if}
                  </td>
                {/if}
                {#if sections.length > 1}
                  <td class="px-2 py-2 whitespace-nowrap">
                    <button
                      type="button"
                      class="text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                      on:click|stopPropagation={() => (sectionFilter = sectionFilter === f.sectionKey ? '' : f.sectionKey)}
                    >{f.sectionTitle}</button>
                  </td>
                {/if}
                {#if showLifecycle}
                  <td class="px-2 py-2 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{formatDate(f.firstSeen)}</td>
                  <td class="px-2 py-2 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">{formatDate(f.lastSeen)}</td>
                {/if}
                {#if showResolvedCol}
                  <td class="px-2 py-2 whitespace-nowrap text-xs {f.resolvedAt ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}">{formatDate(f.resolvedAt)}</td>
                {/if}
                <td class="px-2 py-2 text-right">
                  <button
                    type="button"
                    class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-expanded={expandedKey === k}
                    aria-label={expandedKey === k ? $_('common.collapse') : $_('common.expand')}
                    on:click={() => toggleExpand(f)}
                  >
                    {#if expandedKey === k}<ChevronDown size={15} />{:else}<ChevronRight size={15} />{/if}
                  </button>
                </td>
              </tr>
              {#if expandedKey === k}
                <tr class="bg-gray-50 dark:bg-gray-800/40">
                  <td class="w-1 p-0 {f.severity ? severityRail[f.severity] || '' : 'bg-gray-300 dark:bg-gray-600'}"></td>
                  <td colspan="12" class="px-3 py-3 text-sm space-y-2">
                    {#if f.description}
                      <div>
                        <div class="text-[11px] uppercase tracking-wider text-gray-400 mb-0.5">{$_('complianceReports.description')}</div>
                        <p class="text-gray-700 dark:text-gray-200">{f.description}</p>
                      </div>
                    {/if}
                    {#if f.remediation}
                      <div>
                        <div class="text-[11px] uppercase tracking-wider text-gray-400 mb-0.5">{$_('complianceReports.remediation')}</div>
                        <p class="text-gray-700 dark:text-gray-200">{f.remediation}</p>
                      </div>
                    {/if}
                    {#if (f.affectedHosts || []).length}
                      <div>
                        <div class="text-[11px] uppercase tracking-wider text-gray-400 mb-0.5">{$_('complianceReports.affectedHosts')}</div>
                        <div class="flex flex-wrap gap-1">
                          {#each f.affectedHosts as h}
                            <span class="px-1.5 py-0.5 text-[11px] font-mono border border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-300">{h}</span>
                          {/each}
                        </div>
                      </div>
                    {/if}
                    {#if f.evidence}
                      <div class="text-xs text-gray-500 dark:text-gray-400 break-all">{f.evidence}</div>
                    {/if}
                    {#if !f.description && !f.remediation && !(f.affectedHosts || []).length && !f.evidence}
                      <p class="text-xs text-gray-400 italic">{$_('complianceReports.findings.noDetail')}</p>
                    {/if}
                  </td>
                </tr>
              {/if}
            {/each}
          {/each}
        </tbody>
      </table>
    </div>

    {#if paginate}
      <div class="flex items-center justify-end gap-3 mt-3 text-sm">
        <button type="button" class="btn btn-secondary !py-1 !px-2 text-xs disabled:opacity-40" disabled={page <= 1} on:click={() => (page = Math.max(1, page - 1))}>
          {$_('common.previous')}
        </button>
        <span class="text-xs text-gray-500 dark:text-gray-400">{$_('common.page')} {page} {$_('common.of')} {totalPages}</span>
        <button type="button" class="btn btn-secondary !py-1 !px-2 text-xs disabled:opacity-40" disabled={page >= totalPages} on:click={() => (page = Math.min(totalPages, page + 1))}>
          {$_('common.next')}
        </button>
      </div>
    {/if}
  {/if}
{/if}
